import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { useT } from '@/i18n'

import { CommandPalette } from './CommandPalette'
import { MobileNav, Sidebar } from './Sidebar'
import { Topbar, type AdminEnvironment } from './Topbar'
import { isDemoMode } from '@avexa/config/demo-mode'

const COLLAPSE_STORAGE_KEY = 'avexa.admin.sidebarCollapsed'

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * Which build is this? The badge exists so nobody demonstrates the wrong one to
 * a client, or files a bug against the demo thinking it was the dev site.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 RETURNS `null` WHEN NOTHING SAYS, AND IT USED TO RETURN `'staging'`.
 *
 * `VITE_ENVIRONMENT` is not set on the Netlify site, so the fallback at the end
 * of this function ran on every request — and the client's own production panel
 * at <https://admin-avexamarketplace.netlify.app> carried a **"Staging"** badge
 * on every screen. A badge is a claim about the deployment; the one state this
 * function must never invent is the one it is there to report.
 *
 * ⚠️ `null` IS NOT `'production'` EITHER. Defaulting the other way would be the
 * same mistake pointing at a different word, and would put a loud destructive
 * badge on a local `vite preview`. Not knowing is a third thing, and it renders
 * as no badge at all — which `Topbar` reads as "say nothing", not as "say it is
 * fine".
 *
 * THE TWO REAL SIGNALS ARE KEPT, because neither is a guess: `isDemoMode()` is
 * an explicit flag, and `import.meta.env.DEV` is true only under the dev server.
 *
 * To get the badge back on the hosted panel, set `VITE_ENVIRONMENT=production`
 * in that Netlify site's environment. Nothing in the code can supply it.
 * ═══════════════════════════════════════════════════════════════════════════
 */
const NAMED_ENVIRONMENTS: readonly AdminEnvironment[] = [
  'development',
  'demo',
  'staging',
  'production',
]

function resolveEnvironment(): AdminEnvironment | null {
  if (isDemoMode()) return 'demo'
  if (import.meta.env.DEV) return 'development'

  const configured = import.meta.env.VITE_ENVIRONMENT?.trim()
  // ⚠️ Checked against the whole list, not against `production | staging` as it
  // was: `VITE_ENVIRONMENT=demo` was silently relabelled "Staging", and there is
  // a `topbar.environment.demo` string for it in both catalogues.
  const named = NAMED_ENVIRONMENTS.find((value) => value === configured)

  return named ?? null
}

/**
 * The application shell: rail + topbar + routed content, with the command
 * palette living at this level so ⌘K works from anywhere inside the panel.
 */
export function AdminShell(): ReactNode {
  const t = useT()
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const location = useLocation()

  const toggleCollapsed = useCallback(() => {
    setCollapsed((value) => {
      const next = !value
      try {
        window.localStorage.setItem(COLLAPSE_STORAGE_KEY, String(next))
      } catch {
        // Non-fatal: the rail simply reopens expanded next session.
      }
      return next
    })
  }, [])

  // ⌘K / Ctrl+K anywhere, plus "/" when not already typing.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const isPaletteShortcut = event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)
      if (!isPaletteShortcut) return
      event.preventDefault()
      setPaletteOpen((value) => !value)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  // A route change closes any transient layer and returns focus to the top.
  useEffect(() => {
    setMobileNavOpen(false)
    setPaletteOpen(false)
  }, [location.pathname])

  return (
    <div className="flex min-h-svh w-full bg-background">
      <Sidebar collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
      <MobileNav open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

      <div className="flex min-w-0 flex-1 flex-col">
        <a
          href="#admin-main"
          className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-body-sm focus:text-primary-foreground"
        >
          {/* Skip link — the first tab stop on every page. */}
          {t('common.skipToContent')}
        </a>

        <Topbar
          onOpenNav={() => setMobileNavOpen(true)}
          onOpenPalette={() => setPaletteOpen(true)}
          environment={resolveEnvironment()}
        />

        <main
          id="admin-main"
          tabIndex={-1}
          className="mx-auto w-full max-w-container flex-1 px-3 py-4 outline-none sm:px-4 sm:py-5 lg:px-6"
        >
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  )
}
